import React, { useEffect, useState, useRef } from 'react';
import { Button, Badge, Form, Modal } from 'react-bootstrap';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiUpload, FiSettings, FiFileText, FiHelpCircle, FiBookOpen, FiLayers, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { FaGripVertical } from 'react-icons/fa';
import { stripHtml } from '@components/common/RichText';
import { ContentBlockEditor } from './ContentBlockEditor';
import { QuestionBlockEditor } from './QuestionBlockEditor';
import { ContentImportModal } from './ContentImportModal';
import { QuestionImportModal } from './QuestionImportModal';
import { ModuleEditorModal } from './ModuleEditorModal';
import { repositoryFactory } from '@data';

// Question type icons
const QUESTION_TYPE_ICONS = {
  'multiple-choice': '🔘',
  'multi-select': '☑️',
  'true-false': '⊤⊥',
  'fill-in': '___',
  'matching': '↔',
  'drag-drop': '⇅',
};

const getOptionText = (option) => {
  if (typeof option === 'string') return option;
  return option?.text || option?.answer_text || '';
};

const getOptionId = (option) => {
  if (!option || typeof option !== 'object') return '';
  return String(option.id || option.value || '').trim();
};

const normalizeText = (value) => stripHtml(String(value || '')).trim().toLowerCase();

const normalizeId = (value) => {
  if (value && typeof value === 'object') {
    return String(value.id || value.value || '').trim();
  }
  return String(value || '').trim();
};

const hasTextValue = (value) => normalizeText(value).length > 0;

function hasValidCorrectAnswer(question) {
  if (!question) return false;

  const questionType = question.type;
  const optionValues = Array.isArray(question.options)
    ? question.options.map(getOptionText).filter(Boolean)
    : [];
  const optionSet = new Set(optionValues.map(normalizeText).filter(Boolean));
  const optionIdSet = new Set(
    (Array.isArray(question.options) ? question.options : [])
      .map(getOptionId)
      .filter(Boolean)
  );

  if (questionType === 'multiple-choice' || questionType === 'true-false') {
    const answer = normalizeText(question.correctAnswer);
    const answerId = normalizeId(question.correctAnswer);
    if (!answer) return false;
    // Some legacy records can have missing options; if options exist, enforce membership.
    return (optionSet.size === 0 && optionIdSet.size === 0)
      || optionSet.has(answer)
      || optionIdSet.has(answerId);
  }

  if (questionType === 'multi-select') {
    if (!Array.isArray(question.correctAnswer) || question.correctAnswer.length === 0) return false;
    const answers = question.correctAnswer.map(normalizeText).filter(Boolean);
    const answerIds = question.correctAnswer.map(normalizeId).filter(Boolean);
    if (answers.length === 0) return false;
    return (optionSet.size === 0 && optionIdSet.size === 0)
      || answers.every(answer => optionSet.has(answer))
      || answerIds.every(answerId => optionIdSet.has(answerId));
  }

  if (questionType === 'fill-in') {
    return hasTextValue(question.correctAnswer);
  }

  if (questionType === 'matching') {
    const leftItems = Array.isArray(question.leftItems) ? question.leftItems : [];
    const rightIds = new Set(
      (Array.isArray(question.rightItems) ? question.rightItems : [])
        .map(item => item?.id)
        .filter(Boolean)
    );
    const answerMap =
      question.correctAnswer &&
      typeof question.correctAnswer === 'object' &&
      !Array.isArray(question.correctAnswer)
        ? question.correctAnswer
        : {};

    if (leftItems.length === 0) {
      return Object.keys(answerMap).length > 0;
    }

    return leftItems.every(item => {
      const leftId = item?.id;
      const mappedRightId = leftId ? answerMap[leftId] : null;
      return Boolean(leftId && mappedRightId && rightIds.has(mappedRightId));
    });
  }

  if (questionType === 'drag-drop') {
    const itemIds = (Array.isArray(question.items) ? question.items : [])
      .map(item => item?.id)
      .filter(Boolean);
    const correctOrder = Array.isArray(question.correctAnswer)
      ? question.correctAnswer.filter(Boolean)
      : [];

    if (correctOrder.length === 0) return false;
    if (itemIds.length === 0) return true;
    if (correctOrder.length !== itemIds.length) return false;

    const itemSet = new Set(itemIds);
    return correctOrder.every(itemId => itemSet.has(itemId));
  }

  if (Array.isArray(question.correctAnswer)) {
    return question.correctAnswer.some(answer => hasTextValue(answer));
  }

  if (question.correctAnswer && typeof question.correctAnswer === 'object') {
    return Object.keys(question.correctAnswer).length > 0;
  }

  return hasTextValue(question.correctAnswer);
}

// ── Content Preview Modal ─────────────────────────────────────────────────────
function ContentPreviewModal({ show, onHide, content }) {
  if (!content) return null;
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="pb-2">
        <div>
          <Modal.Title className="fs-5 fw-semibold">{content.title}</Modal.Title>
          <div className="d-flex flex-wrap gap-1 mt-1">
            <Badge bg="info" className="fw-normal">{content.contentType || 'text'}</Badge>
            {content.showInInitial !== false && <Badge bg="light" text="dark" className="fw-normal border">Initial</Badge>}
            {content.showInReview && <Badge bg="light" text="dark" className="fw-normal border">Review</Badge>}
            {content.tags?.map(tag => (
              <Badge key={tag} bg="secondary" className="fw-normal">{tag}</Badge>
            ))}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div
          className="content-preview-body"
          dangerouslySetInnerHTML={{ __html: content.content || '<p class="text-muted fst-italic">No preview available</p>' }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Question Preview Modal ────────────────────────────────────────────────────
function QuestionPreviewModal({ show, onHide, question }) {
  if (!question) return null;
  const typeIcon = QUESTION_TYPE_ICONS[question.type] || '❓';
  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <Modal.Header closeButton className="pb-2">
        <div className="d-flex flex-wrap gap-1 align-items-center">
          <Badge bg="warning" text="dark" className="fw-normal fs-6">{typeIcon} {question.type}</Badge>
          {question.difficulty && <Badge bg="light" text="dark" className="fw-normal border">{question.difficulty}</Badge>}
          {question.tags?.map(tag => (
            <Badge key={tag} bg="secondary" className="fw-normal">{tag}</Badge>
          ))}
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* Question text */}
        <div
          className="question-preview-text mb-4"
          dangerouslySetInnerHTML={{ __html: question.text || '<em class="text-muted">No question text</em>' }}
        />

        {/* Options for MC / multi-select / true-false */}
        {question.options && question.options.length > 0 && (
          <div className="question-options">
            {question.options.map((option, i) => {
              const optText = typeof option === 'string' ? option : option.text || '';
              const isCorrect = question.type === 'multi-select'
                ? Array.isArray(question.correctAnswer) && question.correctAnswer.includes(optText)
                : question.correctAnswer === optText;
              return (
                <div key={i} className={`question-option${isCorrect ? ' correct' : ''}`}>
                  <span className="option-indicator">{isCorrect ? '✓' : String.fromCharCode(65 + i)}</span>
                  <span dangerouslySetInnerHTML={{ __html: optText }} />
                </div>
              );
            })}
          </div>
        )}

        {/* Fill-in answer */}
        {question.type === 'fill-in' && (
          <div className="mt-2">
            <small className="text-muted fw-semibold">Answer:</small>
            <div className="p-2 bg-light rounded mt-1 fw-medium">{question.correctAnswer}</div>
          </div>
        )}

        {/* Explanation */}
        {question.explanation && (
          <div className="mt-3 pt-3 border-top">
            <small className="text-muted fw-semibold">Explanation:</small>
            <div className="mt-1" dangerouslySetInnerHTML={{ __html: question.explanation }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export function CourseManager({ course, onRefresh }) {
  // Selection & navigation
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [activeTab, setActiveTab] = useState('content');

  // Editor modals
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [currentModuleId, setCurrentModuleId] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showContentImportModal, setShowContentImportModal] = useState(false);
  const [showQuestionImportModal, setShowQuestionImportModal] = useState(false);

  // Preview
  const [previewContent, setPreviewContent] = useState(null);
  const [previewQuestion, setPreviewQuestion] = useState(null);

  // Settings
  const [questionPoolScope, setQuestionPoolScope] = useState('unlocked');
  const [showSettings, setShowSettings] = useState(false);

  // Drag-and-drop state
  const dragItemRef = useRef(null);
  const dragTypeRef = useRef(null); // 'module' | 'content' | 'question'
  const [dragOverId, setDragOverId] = useState(null);

  const repo = repositoryFactory.getCourseRepository();

  // Sync settings state and maintain valid module selection
  useEffect(() => {
    if (!course) return;
    setQuestionPoolScope(course.metadata?.questionPoolScope || 'unlocked');
  }, [course?.id, course?.metadata?.questionPoolScope]);

  useEffect(() => {
    if (!course?.sections || course.sections.length === 0) return;
    if (!selectedModuleId || !course.sections.find(s => s.id === selectedModuleId)) {
      setSelectedModuleId(course.sections[0].id);
    }
  }, [course?.sections]);

  const selectedModule = course?.sections?.find(s => s.id === selectedModuleId) || null;

  // ── Settings ──────────────────────────────────────────────────────────────
  const handleQuestionPoolScopeChange = async (value) => {
    if (!course) return;
    setQuestionPoolScope(value);
    await repo.updateCourse(course.id, {
      metadata: { ...(course.metadata || {}), questionPoolScope: value },
    });
    onRefresh();
  };

  // ── Module handlers ───────────────────────────────────────────────────────
  const handleAddModule = () => {
    setEditingModule({
      id: `module-${Date.now()}`,
      title: '',
      description: '',
      index: course?.sections?.length || 0,
      examples: [],
      questions: [],
    });
    setShowModuleModal(true);
  };

  const handleEditModule = (module, e) => {
    if (e) e.stopPropagation();
    setEditingModule(module);
    setShowModuleModal(true);
  };

  const handleSaveModule = async (moduleData) => {
    await repo.upsertSection(course.id, moduleData);
    setShowModuleModal(false);
    setEditingModule(null);
    onRefresh();
  };

  const handleDeleteModule = async (moduleId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this module? All content and questions will be lost.')) return;
    await repo.deleteSection(course.id, moduleId);
    if (selectedModuleId === moduleId) {
      const remaining = course.sections.filter(s => s.id !== moduleId);
      setSelectedModuleId(remaining.length > 0 ? remaining[0].id : null);
    }
    onRefresh();
  };

  // ── Module drag-and-drop ──────────────────────────────────────────────────
  const handleModuleDragStart = (e, moduleId) => {
    dragItemRef.current = moduleId;
    dragTypeRef.current = 'module';
    e.dataTransfer.setData('text/plain', moduleId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDragOver = (e, moduleId) => {
    e.preventDefault();
    if (dragTypeRef.current !== 'module' || dragItemRef.current === moduleId) return;
    setDragOverId(moduleId);
  };

  const handleModuleDrop = async (e, targetModuleId) => {
    e.preventDefault();
    setDragOverId(null);
    if (dragTypeRef.current !== 'module' || dragItemRef.current === targetModuleId) return;
    const sections = [...course.sections];
    const fromIndex = sections.findIndex(s => s.id === dragItemRef.current);
    const toIndex = sections.findIndex(s => s.id === targetModuleId);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = sections.splice(fromIndex, 1);
    sections.splice(toIndex, 0, moved);
    const reorderedSections = sections.map((section, index) => ({ ...section, index }));
    const updatedCourse = { ...course.toJSON(), sections: reorderedSections };
    await repo.saveCourse(updatedCourse);
    dragItemRef.current = null;
    onRefresh();
  };

  // ── Content handlers ──────────────────────────────────────────────────────
  const handleAddContent = (moduleId) => {
    setCurrentModuleId(moduleId);
    setEditingContent(null);
    setShowContentModal(true);
  };

  const handleEditContent = (content, moduleId, e) => {
    if (e) e.stopPropagation();
    setCurrentModuleId(moduleId);
    setEditingContent(content);
    setShowContentModal(true);
  };

  const handleSaveContent = async (contentData) => {
    const module = course.sections.find(s => s.id === currentModuleId);
    if (!module) return;
    const examples = [...(module.examples || [])];
    const existingIndex = examples.findIndex(ex => ex.id === contentData.id);
    if (existingIndex >= 0) {
      examples[existingIndex] = contentData;
    } else {
      examples.push(contentData);
    }
    await repo.upsertSection(course.id, { ...module, examples });
    setShowContentModal(false);
    setEditingContent(null);
    setCurrentModuleId(null);
    onRefresh();
  };

  const handleDeleteContent = async (moduleId, contentId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this content block?')) return;
    const module = course.sections.find(s => s.id === moduleId);
    if (!module) return;
    const examples = (module.examples || []).filter(ex => ex.id !== contentId);
    await repo.upsertSection(course.id, { ...module, examples });
    onRefresh();
  };

  // ── Content drag-and-drop ─────────────────────────────────────────────────
  const handleContentDragStart = (e, contentId) => {
    dragItemRef.current = contentId;
    dragTypeRef.current = 'content';
    e.dataTransfer.setData('text/plain', contentId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleContentDragOver = (e, contentId) => {
    e.preventDefault();
    if (dragTypeRef.current !== 'content' || dragItemRef.current === contentId) return;
    setDragOverId(contentId);
  };

  const handleContentDrop = async (e, targetContentId) => {
    e.preventDefault();
    setDragOverId(null);
    if (dragTypeRef.current !== 'content' || dragItemRef.current === targetContentId) return;
    const module = course.sections.find(s => s.id === selectedModuleId);
    if (!module) return;
    const examples = [...(module.examples || [])];
    const fromIndex = examples.findIndex(ex => ex.id === dragItemRef.current);
    const toIndex = examples.findIndex(ex => ex.id === targetContentId);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = examples.splice(fromIndex, 1);
    examples.splice(toIndex, 0, moved);
    const reorderedExamples = examples.map((example, index) => ({ ...example, orderIndex: index }));
    await repo.upsertSection(course.id, { ...module, examples: reorderedExamples });
    dragItemRef.current = null;
    onRefresh();
  };

  // ── Question handlers ─────────────────────────────────────────────────────
  const handleAddQuestion = (moduleId) => {
    setCurrentModuleId(moduleId);
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question, moduleId, e) => {
    if (e) e.stopPropagation();
    setCurrentModuleId(moduleId);
    setEditingQuestion(question);
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async (questionData) => {
    const module = course.sections.find(s => s.id === currentModuleId);
    if (!module) return;
    const questions = [...(module.questions || [])];
    const existingIndex = questions.findIndex(q => q.id === questionData.id);
    if (existingIndex >= 0) {
      questions[existingIndex] = questionData;
    } else {
      questions.push(questionData);
    }
    await repo.upsertSection(course.id, { ...module, questions });
    setShowQuestionModal(false);
    setEditingQuestion(null);
    setCurrentModuleId(null);
    onRefresh();
  };

  const handleDeleteQuestion = async (moduleId, questionId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this question block?')) return;
    const module = course.sections.find(s => s.id === moduleId);
    if (!module) return;
    const questions = (module.questions || []).filter(q => q.id !== questionId);
    await repo.upsertSection(course.id, { ...module, questions });
    onRefresh();
  };

  // ── Question drag-and-drop ────────────────────────────────────────────────
  const handleQuestionDragStart = (e, questionId) => {
    dragItemRef.current = questionId;
    dragTypeRef.current = 'question';
    e.dataTransfer.setData('text/plain', questionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleQuestionDragOver = (e, questionId) => {
    e.preventDefault();
    if (dragTypeRef.current !== 'question' || dragItemRef.current === questionId) return;
    setDragOverId(questionId);
  };

  const handleQuestionDrop = async (e, targetQuestionId) => {
    e.preventDefault();
    setDragOverId(null);
    if (dragTypeRef.current !== 'question' || dragItemRef.current === targetQuestionId) return;
    const module = course.sections.find(s => s.id === selectedModuleId);
    if (!module) return;
    const questions = [...(module.questions || [])];
    const fromIndex = questions.findIndex(q => q.id === dragItemRef.current);
    const toIndex = questions.findIndex(q => q.id === targetQuestionId);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = questions.splice(fromIndex, 1);
    questions.splice(toIndex, 0, moved);
    const reorderedQuestions = questions.map((question, index) => ({ ...question, orderIndex: index }));
    await repo.upsertSection(course.id, { ...module, questions: reorderedQuestions });
    dragItemRef.current = null;
    onRefresh();
  };

  const handleImportContent = async (importedData) => {
    let updatedCourse = { ...course.toJSON() };

    for (const item of importedData) {
      // Find or create section
      let section = updatedCourse.sections?.find(s => s.title.toLowerCase() === item.section.toLowerCase());

      if (!section) {
        // Create new section
        section = {
          id: `section-${Date.now()}-${Math.random()}`,
          title: item.section,
          description: '',
          index: updatedCourse.sections?.length || 0,
          examples: [],
          questions: [],
        };
        updatedCourse.sections = updatedCourse.sections || [];
        updatedCourse.sections.push(section);
      }

      // Build the content object
      const contentBlock = {
        id: `content-${Date.now()}-${Math.random()}`,
        title: item.title,
        content: item.content,
        contentType: item.contentType.toLowerCase(),
        showInInitial: item.showInInitial !== false, // Default true
        showInReview: item.showInReview === true, // Default false
        tags: item.tags ? item.tags.split(';').map(t => t.trim()).filter(Boolean) : [],
      };

      // Add content to section
      const sectionIndex = updatedCourse.sections.findIndex(s => s.id === section.id);
      if (sectionIndex >= 0) {
        updatedCourse.sections[sectionIndex].examples = updatedCourse.sections[sectionIndex].examples || [];
        updatedCourse.sections[sectionIndex].examples.push(contentBlock);
      }
    }

    // Save the updated course
    await repo.saveCourse(updatedCourse);
    setShowContentImportModal(false);
    onRefresh();
  };

  const handleImportQuestions = async (importedData) => {
    let updatedCourse = { ...course.toJSON() };

    for (const row of importedData) {
      // Find or create section by title (case-insensitive)
      let section = updatedCourse.sections?.find(
        (s) => s.title?.toLowerCase() === (row.section || '').toLowerCase()
      );

      if (!section) {
        section = {
          id: `section-${Date.now()}-${Math.random()}`,
          title: row.section,
          description: '',
          index: updatedCourse.sections?.length || 0,
          examples: [],
          questions: [],
        };
        updatedCourse.sections = updatedCourse.sections || [];
        updatedCourse.sections.push(section);
      }

      const type = (row.type || '').toLowerCase();
      const baseQuestion = {
        id: `question-${Date.now()}-${Math.random()}`,
        type,
        text: row.question || '',
        explanation: row.explanation || '',
        tags: row.tags
          ? row.tags.split(';').map((t) => t.trim()).filter(Boolean)
          : [],
        difficulty: row.difficulty || 'medium',
      };

      let questionToAdd = { ...baseQuestion };

      if (type === 'multiple-choice') {
        const options = (row.options || '')
          .split(';')
          .map((o) => o.trim())
          .filter(Boolean);
        questionToAdd.options = options;
        questionToAdd.correctAnswer = (row.correctAnswer || row.correct_answer || '').trim();
      } else if (type === 'multi-select') {
        const options = (row.options || '')
          .split(';')
          .map((o) => o.trim())
          .filter(Boolean);
        const answers = (row.correctAnswer || row.correct_answer || '')
          .split(';')
          .map((a) => a.trim())
          .filter(Boolean);
        questionToAdd.options = options;
        questionToAdd.correctAnswer = answers;
      } else if (type === 'true-false') {
        questionToAdd.options = ['True', 'False'];
        const ans = (row.correctAnswer || row.correct_answer || '').trim().toLowerCase();
        questionToAdd.correctAnswer = ans === 'true' ? 'True' : 'False';
      } else if (type === 'fill-in') {
        questionToAdd.options = [];
        questionToAdd.correctAnswer = (row.correctAnswer || row.correct_answer || '').trim();
      } else if (type === 'matching') {
        const leftTexts = (row.leftItems || row.left_items || '')
          .split(';')
          .map((t) => t.trim())
          .filter(Boolean);
        const rightTexts = (row.rightItems || row.right_items || '')
          .split(';')
          .map((t) => t.trim())
          .filter(Boolean);
        const leftItems = leftTexts.map((text) => ({ id: `left${Date.now()}-${Math.random()}`, text }));
        const rightItems = rightTexts.map((text) => ({ id: `right${Date.now()}-${Math.random()}`, text }));
        const mapping = {};
        for (let i = 0; i < leftItems.length; i++) {
          const li = leftItems[i];
          const ri = rightItems[i];
          if (li && ri) mapping[li.id] = ri.id;
        }
        questionToAdd.leftItems = leftItems;
        questionToAdd.rightItems = rightItems;
        questionToAdd.correctAnswer = mapping;
      } else if (type === 'drag-drop') {
        const itemTexts = (row.items || '')
          .split(';')
          .map((t) => t.trim())
          .filter(Boolean);
        const items = itemTexts.map((text) => ({ id: `step${Date.now()}-${Math.random()}`, text }));
        const orderTexts = (row.correctAnswer || row.correct_answer || '')
          .split(';')
          .map((t) => t.trim())
          .filter(Boolean);
        // Map provided order texts to item IDs; fallback to given item order
        let correctOrder = [];
        if (orderTexts.length > 0) {
          for (const ot of orderTexts) {
            const found = items.find((it) => it.text === ot);
            if (found) correctOrder.push(found.id);
          }
        }
        if (correctOrder.length === 0) {
          correctOrder = items.map((it) => it.id);
        }
        questionToAdd.items = items;
        questionToAdd.correctAnswer = correctOrder;
      }

      // Persist in section
      const sectionIndex = updatedCourse.sections.findIndex((s) => s.id === section.id);
      if (sectionIndex >= 0) {
        updatedCourse.sections[sectionIndex].questions = updatedCourse.sections[sectionIndex].questions || [];
        updatedCourse.sections[sectionIndex].questions.push(questionToAdd);
      }
    }

    await repo.saveCourse(updatedCourse);
    setShowQuestionImportModal(false);
    onRefresh();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="cm-layout">

      {/* Header */}
      <div className="cm-header">
        <div>
          <h5 className="mb-0 fw-semibold">📚 Course Structure</h5>
          <small className="text-muted">Organize content into modules — drag to reorder</small>
        </div>
        <div className="cm-header-actions">
          <Button variant="outline-secondary" size="sm" onClick={() => setShowSettings(s => !s)}>
            <FiSettings className="me-1" />
            Settings
          </Button>
          <Button variant="outline-success" size="sm" onClick={() => setShowContentImportModal(true)}>
            <FiUpload className="me-1" />
            Import Content
          </Button>
          <Button variant="outline-warning" size="sm" onClick={() => setShowQuestionImportModal(true)}>
            <FiUpload className="me-1" />
            Import Questions
          </Button>
          <Button variant="primary" size="sm" onClick={handleAddModule}>
            <FiPlus className="me-1" />
            Add Module
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="cm-settings-panel">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <div className="fw-semibold small">Question Pool Scope</div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                Controls which sections are included in question-only practice.
              </div>
            </div>
            <Form.Select
              value={questionPoolScope}
              onChange={(e) => handleQuestionPoolScopeChange(e.target.value)}
              style={{ maxWidth: 280 }}
              size="sm"
            >
              <option value="unlocked">Unlocked sections only (default)</option>
              <option value="all">All sections</option>
            </Form.Select>
          </div>
        </div>
      )}

      {/* Two-panel body */}
      <div className="cm-body">

        {/* ── Left: Module Sidebar ── */}
        <div className="cm-sidebar">
          <div className="cm-sidebar-title">
            <span>Modules</span>
            <Badge bg="secondary" pill>{course?.sections?.length || 0}</Badge>
          </div>

          {!course?.sections || course.sections.length === 0 ? (
            <div className="cm-empty-sidebar">
              <FiBookOpen size={32} className="text-muted mb-2" />
              <p className="text-muted small mb-2">No modules yet</p>
              <Button variant="outline-primary" size="sm" onClick={handleAddModule}>
                <FiPlus className="me-1" /> Add First Module
              </Button>
            </div>
          ) : (
            course.sections.map((module) => (
              <div
                key={module.id}
                className={`cm-module-item${selectedModuleId === module.id ? ' active' : ''}${dragOverId === module.id ? ' drag-over' : ''}`}
                onClick={() => setSelectedModuleId(module.id)}
                draggable
                onDragStart={(e) => handleModuleDragStart(e, module.id)}
                onDragOver={(e) => handleModuleDragOver(e, module.id)}
                onDrop={(e) => handleModuleDrop(e, module.id)}
                onDragLeave={() => setDragOverId(null)}
                onDragEnd={() => { setDragOverId(null); dragItemRef.current = null; }}
              >
                <span className="cm-drag-handle" title="Drag to reorder">
                  <FaGripVertical />
                </span>
                <div className="cm-module-info">
                  <div className="cm-module-title">{module.title}</div>
                  {module.description && (
                    <div className="cm-module-desc">{module.description}</div>
                  )}
                  <div className="cm-module-counts">
                    <span><FiFileText size={11} /> {module.examples?.length || 0}</span>
                    <span><FiHelpCircle size={11} /> {module.questions?.length || 0}</span>
                  </div>
                </div>
                <div className="cm-module-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="cm-icon-btn"
                    title="Edit module"
                    onClick={(e) => handleEditModule(module, e)}
                  >
                    <FiEdit2 size={13} />
                  </button>
                  <button
                    className="cm-icon-btn text-danger"
                    title="Delete module"
                    onClick={(e) => handleDeleteModule(module.id, e)}
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Right: Module Detail ── */}
        <div className="cm-main">
          {!selectedModule ? (
            <div className="cm-empty-main">
              <FiLayers size={48} className="text-muted mb-3" />
              <h6 className="text-muted">Select a module to edit</h6>
              <p className="text-muted small">Choose a module from the sidebar or add a new one.</p>
            </div>
          ) : (
            <>
              {/* Module header */}
              <div className="cm-module-header">
                <div>
                  <h6 className="mb-0 fw-semibold">{selectedModule.title}</h6>
                  {selectedModule.description && (
                    <p className="text-muted small mb-0 mt-1">{selectedModule.description}</p>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={() => handleEditModule(selectedModule)}>
                    <FiEdit2 className="me-1" /> Edit
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteModule(selectedModule.id)}>
                    <FiTrash2 className="me-1" /> Delete
                  </Button>
                </div>
              </div>

              {/* Content / Questions tabs */}
              <div className="cm-tabs">
                <button
                  className={`cm-tab${activeTab === 'content' ? ' active' : ''}`}
                  onClick={() => setActiveTab('content')}
                >
                  <FiFileText />
                  Content
                  <Badge
                    bg={activeTab === 'content' ? 'primary' : 'secondary'}
                    pill
                    className="ms-1"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {selectedModule.examples?.length || 0}
                  </Badge>
                </button>
                <button
                  className={`cm-tab${activeTab === 'questions' ? ' active' : ''}`}
                  onClick={() => setActiveTab('questions')}
                >
                  <FiHelpCircle />
                  Questions
                  <Badge
                    bg={activeTab === 'questions' ? 'warning' : 'secondary'}
                    text={activeTab === 'questions' ? 'dark' : undefined}
                    pill
                    className="ms-1"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {selectedModule.questions?.length || 0}
                  </Badge>
                </button>
              </div>

              {/* ── Content Tab ── */}
              {activeTab === 'content' && (
                <div className="cm-tab-panel">
                  <div className="cm-tab-toolbar">
                    <span className="text-muted small">Drag <FaGripVertical style={{ verticalAlign: 'middle' }} /> to reorder</span>
                    <div className="d-flex gap-2">
                      <Button variant="outline-success" size="sm" onClick={() => setShowContentImportModal(true)}>
                        <FiUpload className="me-1" /> Import
                      </Button>
                      <Button variant="primary" size="sm" onClick={() => handleAddContent(selectedModule.id)}>
                        <FiPlus className="me-1" /> Add Content
                      </Button>
                    </div>
                  </div>

                  {!selectedModule.examples || selectedModule.examples.length === 0 ? (
                    <div className="cm-empty-list">
                      <FiFileText size={32} className="text-muted mb-2" />
                      <p className="text-muted mb-2">No content blocks yet</p>
                      <Button variant="outline-primary" size="sm" onClick={() => handleAddContent(selectedModule.id)}>
                        <FiPlus className="me-1" /> Add First Content Block
                      </Button>
                    </div>
                  ) : (
                    <div className="cm-item-list">
                      {selectedModule.examples.map((content) => (
                        <div
                          key={content.id}
                          className={`cm-list-item${dragOverId === content.id ? ' drag-over' : ''}`}
                          draggable
                          onDragStart={(e) => handleContentDragStart(e, content.id)}
                          onDragOver={(e) => handleContentDragOver(e, content.id)}
                          onDrop={(e) => handleContentDrop(e, content.id)}
                          onDragLeave={() => setDragOverId(null)}
                          onDragEnd={() => { setDragOverId(null); dragItemRef.current = null; }}
                        >
                          <span className="cm-drag-handle" title="Drag to reorder">
                            <FaGripVertical />
                          </span>
                          <span className="cm-item-icon">📄</span>
                          <div className="cm-item-info">
                            <div className="cm-item-title">{content.title}</div>
                            <div className="cm-item-meta">
                              <Badge bg="info" className="fw-normal">{content.contentType || 'text'}</Badge>
                              {content.showInInitial !== false && <Badge bg="light" text="dark" className="fw-normal border">Initial</Badge>}
                              {content.showInReview && <Badge bg="light" text="dark" className="fw-normal border">Review</Badge>}
                              {content.tags?.map(tag => (
                                <Badge key={tag} bg="secondary" className="fw-normal">{tag}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="cm-item-actions">
                            <button
                              className="cm-icon-btn"
                              title="Preview content"
                              onClick={() => setPreviewContent(content)}
                            >
                              <FiEye size={15} />
                            </button>
                            <button
                              className="cm-icon-btn"
                              title="Edit content"
                              onClick={(e) => handleEditContent(content, selectedModule.id, e)}
                            >
                              <FiEdit2 size={15} />
                            </button>
                            <button
                              className="cm-icon-btn text-danger"
                              title="Delete content"
                              onClick={(e) => handleDeleteContent(selectedModule.id, content.id, e)}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Questions Tab ── */}
              {activeTab === 'questions' && (
                <div className="cm-tab-panel">
                  <div className="cm-tab-toolbar">
                    <span className="text-muted small">Drag <FaGripVertical style={{ verticalAlign: 'middle' }} /> to reorder</span>
                    <div className="d-flex gap-2">
                      <Button variant="outline-warning" size="sm" onClick={() => setShowQuestionImportModal(true)}>
                        <FiUpload className="me-1" /> Import
                      </Button>
                      <Button variant="warning" size="sm" onClick={() => handleAddQuestion(selectedModule.id)}>
                        <FiPlus className="me-1" /> Add Question
                      </Button>
                    </div>
                  </div>

                  {!selectedModule.questions || selectedModule.questions.length === 0 ? (
                    <div className="cm-empty-list">
                      <FiHelpCircle size={32} className="text-muted mb-2" />
                      <p className="text-muted mb-2">No questions yet</p>
                      <Button variant="outline-warning" size="sm" onClick={() => handleAddQuestion(selectedModule.id)}>
                        <FiPlus className="me-1" /> Add First Question
                      </Button>
                    </div>
                  ) : (
                    <div className="cm-item-list">
                      {selectedModule.questions.map((question) => {
                        const typeIcon = QUESTION_TYPE_ICONS[question.type] || '❓';
                        const questionText = stripHtml(question.text || '');
                        const hasCorrectAnswer = hasValidCorrectAnswer(question);
                        return (
                          <div
                            key={question.id}
                            className={`cm-list-item${dragOverId === question.id ? ' drag-over' : ''}`}
                            draggable
                            onDragStart={(e) => handleQuestionDragStart(e, question.id)}
                            onDragOver={(e) => handleQuestionDragOver(e, question.id)}
                            onDrop={(e) => handleQuestionDrop(e, question.id)}
                            onDragLeave={() => setDragOverId(null)}
                            onDragEnd={() => { setDragOverId(null); dragItemRef.current = null; }}
                          >
                            <span className="cm-drag-handle" title="Drag to reorder">
                              <FaGripVertical />
                            </span>
                            <span className="cm-item-icon">{typeIcon}</span>
                            <div className="cm-item-info">
                              <div className="cm-item-title cm-question-text d-flex align-items-center gap-2">
                                <span className="flex-grow-1">
                                  {questionText.length > 100 ? `${questionText.substring(0, 100)}…` : questionText}
                                </span>
                                {hasCorrectAnswer ? (
                                  <FiCheckCircle
                                    size={16}
                                    className="text-success flex-shrink-0"
                                    title="Correct answer configured"
                                    aria-label="Correct answer configured"
                                  />
                                ) : (
                                  <FiAlertCircle
                                    size={16}
                                    className="text-danger flex-shrink-0"
                                    title="No valid correct answer configured"
                                    aria-label="No valid correct answer configured"
                                  />
                                )}
                              </div>
                              <div className="cm-item-meta">
                                <Badge bg="warning" text="dark" className="fw-normal">{question.type}</Badge>
                                {hasCorrectAnswer ? (
                                  <Badge bg="success" className="fw-normal">✓ Answer set</Badge>
                                ) : (
                                  <Badge bg="danger" className="fw-normal">Missing answer</Badge>
                                )}
                                {question.difficulty && <Badge bg="light" text="dark" className="fw-normal border">{question.difficulty}</Badge>}
                                {question.tags?.map(tag => (
                                  <Badge key={tag} bg="secondary" className="fw-normal">{tag}</Badge>
                                ))}
                              </div>
                            </div>
                            <div className="cm-item-actions">
                              <button
                                className="cm-icon-btn"
                                title="Preview question"
                                onClick={() => setPreviewQuestion(question)}
                              >
                                <FiEye size={15} />
                              </button>
                              <button
                                className="cm-icon-btn"
                                title="Edit question"
                                onClick={(e) => handleEditQuestion(question, selectedModule.id, e)}
                              >
                                <FiEdit2 size={15} />
                              </button>
                              <button
                                className="cm-icon-btn text-danger"
                                title="Delete question"
                                onClick={(e) => handleDeleteQuestion(selectedModule.id, question.id, e)}
                              >
                                <FiTrash2 size={15} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Preview Modals ── */}
      <ContentPreviewModal
        show={!!previewContent}
        onHide={() => setPreviewContent(null)}
        content={previewContent}
      />
      <QuestionPreviewModal
        show={!!previewQuestion}
        onHide={() => setPreviewQuestion(null)}
        question={previewQuestion}
      />

      {/* ── Editor Modals ── */}
      <ModuleEditorModal
        show={showModuleModal}
        onHide={() => { setShowModuleModal(false); setEditingModule(null); }}
        initialValue={editingModule || {}}
        onSave={handleSaveModule}
      />

      <ContentBlockEditor
        show={showContentModal}
        onHide={() => { setShowContentModal(false); setEditingContent(null); setCurrentModuleId(null); }}
        content={editingContent}
        onSave={handleSaveContent}
      />

      <QuestionBlockEditor
        show={showQuestionModal}
        onHide={() => { setShowQuestionModal(false); setEditingQuestion(null); setCurrentModuleId(null); }}
        question={editingQuestion}
        onSave={handleSaveQuestion}
      />

      <ContentImportModal
        show={showContentImportModal}
        onHide={() => setShowContentImportModal(false)}
        course={course}
        onImportSuccess={handleImportContent}
      />

      <QuestionImportModal
        show={showQuestionImportModal}
        onHide={() => setShowQuestionImportModal(false)}
        course={course}
        onImportSuccess={handleImportQuestions}
      />
    </div>
  );
}
